import { MapPin, Phone, Clock, MessageCircle, Siren } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { CLINIC } from "../data/clinic";

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-sa-cream">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="mb-14 max-w-xl">
        <Reveal>
          <SectionLabel testId="contact-label">Visit us</SectionLabel>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-sa-espresso mt-6">
            Come say
            <span className="italic text-sa-bronze"> hello</span>
          </h2>
        </Reveal>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Map */}
        <Reveal>
          <div className="rounded-[32px] overflow-hidden h-[360px] lg:h-full min-h-[360px] border border-sa-espresso/5 shadow-[0_20px_60px_-30px_rgba(59,42,32,0.4)]">
            <iframe
              title="MS Pet Hospital location map"
              data-testid="contact-map"
              src={CLINIC.mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "360px" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>

        {/* Details */}
        <div className="grid sm:grid-cols-2 gap-5">
          <Reveal delay={0.05} className="sm:col-span-2">
            <div className="bg-sa-beige rounded-3xl p-8 border border-sa-espresso/5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-sa-cream flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-sa-bronze" />
                </div>
                <div>
                  <h3 className="font-display text-xl text-sa-espresso mb-1">Address</h3>
                  <p className="font-body text-base text-sa-mocha leading-relaxed">
                    {CLINIC.address.line1}
                    <br />
                    {CLINIC.address.line2}
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <a
              href={`tel:${CLINIC.phoneTel}`}
              data-testid="contact-call-btn"
              className="group block h-full bg-sa-beige rounded-3xl p-8 border border-sa-espresso/5 hover:border-sa-bronze/40 transition-colors duration-300"
            >
              <div className="w-11 h-11 rounded-xl bg-sa-cream flex items-center justify-center mb-5">
                <Phone className="w-5 h-5 text-sa-bronze" />
              </div>
              <h3 className="font-display text-xl text-sa-espresso mb-1">Call us</h3>
              <p className="font-body text-base text-sa-mocha group-hover:text-sa-bronze transition-colors">
                {CLINIC.phoneDisplay}
              </p>
            </a>
          </Reveal>

          <Reveal delay={0.15}>
            <a
              href={CLINIC.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="group block h-full bg-sa-beige rounded-3xl p-8 border border-sa-espresso/5 hover:border-sa-bronze/40 transition-colors duration-300"
            >
              <div className="w-11 h-11 rounded-xl bg-sa-cream flex items-center justify-center mb-5">
                <MessageCircle className="w-5 h-5 text-sa-bronze" />
              </div>
              <h3 className="font-display text-xl text-sa-espresso mb-1">WhatsApp</h3>
              <p className="font-body text-base text-sa-mocha group-hover:text-sa-bronze transition-colors">
                Chat with us
              </p>
            </a>
          </Reveal>

          <Reveal delay={0.2} className="sm:col-span-2">
            <div className="bg-sa-espresso rounded-3xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-sa-bronze/20 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-sa-bronze" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl text-sa-cream mb-2">Opening hours</h3>
                  <p className="font-display text-3xl text-sa-bronze mb-3">Open 24/7</p>
                  {CLINIC.hours.map((h) => (
                    <div
                      key={h.day}
                      className="flex items-center justify-between py-1.5 border-b border-sa-cream/10 last:border-0"
                    >
                      <span className="font-body text-sm text-sa-cream/70">{h.day}</span>
                      <span className="font-body text-sm font-semibold text-sa-cream">
                        {h.time}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.25} className="sm:col-span-2">
            <div
              className="flex items-center gap-3 bg-sa-bronze/10 rounded-2xl px-6 py-4"
              data-testid="contact-amenities"
            >
              <Siren className="w-5 h-5 text-sa-bronze shrink-0" />
              <p className="font-body text-sm text-sa-espresso">
                Emergency walk-ins welcome — any hour of the day or night.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default Contact;
